// src/pages/customer-panel/certificates/CertificatesPage.js
// Student certificates list page

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Chip,
    IconButton,
    Button,
    Paper,
    CircularProgress,
    Alert,
    Tooltip,
    Divider,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Skeleton,
} from '@mui/material';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import VerifiedIcon from '@mui/icons-material/Verified';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import ShareIcon from '@mui/icons-material/Share';
import SortIcon from '@mui/icons-material/Sort';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#10b981';

const STATUS_CONFIG = {
    valid: { label: 'Valid', color: '#10b981', bg: '#dcfce7' },
    active: { label: 'Valid', color: '#10b981', bg: '#dcfce7' },
    expired: { label: 'Expired', color: '#ef4444', bg: '#fee2e2' },
    revoked: { label: 'Revoked', color: '#f59e0b', bg: '#fef3c7' },
};

function getStatusConfig(status) {
    return STATUS_CONFIG[(status || 'valid').toLowerCase()] || STATUS_CONFIG.valid;
}

function formatDate(dateStr) {
    if (!dateStr) return 'N/A';
    try {
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return dateStr;
    }
}

const StatCard = ({ icon: Icon, title, value, color, bgColor }) => (
    <Card
        sx={{
            height: '100%',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: 'none',
            transition: 'all 0.2s',
            '&:hover': { borderColor: color, boxShadow: `0 4px 20px ${color}20` },
        }}
    >
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: '14px',
                        bgcolor: bgColor || `${color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Icon sx={{ color, fontSize: 28 }} />
                </Box>
                <Box>
                    <Typography variant="body2" sx={{ fontFamily, color: '#64748b', fontWeight: 500 }}>
                        {title}
                    </Typography>
                    <Typography variant="h5" sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}>
                        {value}
                    </Typography>
                </Box>
            </Box>
        </CardContent>
    </Card>
);

const CertificateCard = ({ certificate, onView, onDownload, onShare }) => {
    const statusCfg = getStatusConfig(certificate.status);

    return (
        <Card
            sx={{
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
                    borderColor: ACCENT,
                },
            }}
            onClick={() => onView(certificate)}
        >
            {/* Certificate top band */}
            <Box
                sx={{
                    height: 6,
                    background: `linear-gradient(90deg, ${ACCENT} 0%, #059669 100%)`,
                }}
            />
            <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '12px',
                                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <WorkspacePremiumIcon sx={{ color: 'white', fontSize: 24 }} />
                        </Box>
                        <Box>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontFamily,
                                    fontWeight: 700,
                                    color: '#1e293b',
                                    lineHeight: 1.3,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                }}
                            >
                                {certificate.course_name || certificate.course_title || certificate.title}
                            </Typography>
                            <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>
                                {certificate.issuer || 'Course Certificate'}
                            </Typography>
                        </Box>
                    </Box>
                    <Chip
                        label={statusCfg.label}
                        size="small"
                        sx={{
                            fontFamily,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            bgcolor: statusCfg.bg,
                            color: statusCfg.color,
                            height: 24,
                        }}
                    />
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Certificate details */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>
                            Certificate No.
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ fontFamily, fontWeight: 600, color: '#475569', fontFeatureSettings: '"tnum"' }}
                        >
                            {certificate.certificate_number || certificate.cert_id || 'N/A'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>
                            Issue Date
                        </Typography>
                        <Typography variant="caption" sx={{ fontFamily, fontWeight: 600, color: '#475569' }}>
                            {formatDate(certificate.issue_date || certificate.issued_at || certificate.created_at)}
                        </Typography>
                    </Box>
                </Box>

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="View Certificate">
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onView(certificate); }}
                            sx={{
                                bgcolor: `${ACCENT}10`,
                                color: ACCENT,
                                '&:hover': { bgcolor: `${ACCENT}20` },
                            }}
                        >
                            <VisibilityIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Download PDF">
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onDownload(certificate); }}
                            sx={{
                                bgcolor: '#3b82f610',
                                color: '#3b82f6',
                                '&:hover': { bgcolor: '#3b82f620' },
                            }}
                        >
                            <DownloadIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Share">
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onShare(certificate); }}
                            sx={{
                                bgcolor: '#8b5cf610',
                                color: '#8b5cf6',
                                '&:hover': { bgcolor: '#8b5cf620' },
                            }}
                        >
                            <ShareIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </CardContent>
        </Card>
    );
};

export default function CertificatesPage() {
    const navigate = useNavigate();

    const [certificates, setCertificates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [sortBy, setSortBy] = useState('newest');

    const fetchCertificates = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axiosInstance.get('/lms/portal/certificates');
            const data = response.data;
            setCertificates(data.certificates || data.data || data.results || []);
        } catch (err) {
            console.error('Failed to fetch certificates:', err);
            setError(err.response?.data?.message || 'Failed to load certificates.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCertificates();
    }, [fetchCertificates]);

    // Sort certificates
    const sortedCertificates = [...certificates].sort((a, b) => {
        const dateA = new Date(a.issue_date || a.issued_at || a.created_at || 0);
        const dateB = new Date(b.issue_date || b.issued_at || b.created_at || 0);
        return sortBy === 'newest' ? dateB - dateA : dateA - dateB;
    });

    // Stats
    const totalCerts = certificates.length;
    const validCerts = certificates.filter(
        (c) => !c.status || c.status === 'valid' || c.status === 'active'
    ).length;
    const latestDate = certificates.length > 0
        ? formatDate(
            [...certificates].sort((a, b) => {
                const dA = new Date(a.issue_date || a.issued_at || a.created_at || 0);
                const dB = new Date(b.issue_date || b.issued_at || b.created_at || 0);
                return dB - dA;
            })[0]?.issue_date || certificates[0]?.issued_at || certificates[0]?.created_at
        )
        : 'N/A';

    const handleView = useCallback((cert) => {
        navigate(`/customer-panel/certificates/${cert.id || cert._id}`);
    }, [navigate]);

    const handleDownload = useCallback((cert) => {
        const downloadUrl = cert.download_url || cert.pdf_url;
        if (downloadUrl) {
            window.open(downloadUrl, '_blank');
        }
    }, []);

    const handleShare = useCallback((cert) => {
        const shareUrl = cert.share_url || cert.verification_url || window.location.href;
        if (navigator.share) {
            navigator.share({
                title: `Certificate - ${cert.course_name || cert.title}`,
                url: shareUrl,
            }).catch(() => {});
        } else if (navigator.clipboard) {
            navigator.clipboard.writeText(shareUrl);
        }
    }, []);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    borderRadius: { xs: 0, md: '0 0 24px 24px' },
                    px: { xs: 2, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <WorkspacePremiumIcon sx={{ fontSize: 40, color: 'white' }} />
                    <Box>
                        <Typography
                            variant="h4"
                            sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.5rem', md: '2rem' } }}
                        >
                            My Certificates
                        </Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>
                            Your earned certificates and achievements
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {/* Stat Cards */}
                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={4}>
                        <StatCard
                            icon={WorkspacePremiumIcon}
                            title="Total Certificates"
                            value={loading ? '--' : totalCerts}
                            color="#f59e0b"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <StatCard
                            icon={VerifiedIcon}
                            title="Valid"
                            value={loading ? '--' : validCerts}
                            color={ACCENT}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <StatCard
                            icon={CalendarTodayIcon}
                            title="Latest Issued"
                            value={loading ? '--' : latestDate}
                            color="#3b82f6"
                        />
                    </Grid>
                </Grid>

                {/* Sort control */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel sx={{ fontFamily }}>
                            <SortIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                            Sort by
                        </InputLabel>
                        <Select
                            value={sortBy}
                            label="Sort by"
                            onChange={(e) => setSortBy(e.target.value)}
                            sx={{ fontFamily, borderRadius: '10px' }}
                        >
                            <MenuItem value="newest" sx={{ fontFamily }}>Newest First</MenuItem>
                            <MenuItem value="oldest" sx={{ fontFamily }}>Oldest First</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {/* Error */}
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 3, borderRadius: '12px', fontFamily }}
                        action={
                            <Button color="inherit" size="small" onClick={fetchCertificates} sx={{ fontFamily, textTransform: 'none' }}>
                                Retry
                            </Button>
                        }
                    >
                        {error}
                    </Alert>
                )}

                {/* Loading */}
                {loading ? (
                    <Grid container spacing={3}>
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                                    <CardContent sx={{ p: 2.5 }}>
                                        <Skeleton variant="rounded" width={44} height={44} sx={{ mb: 2 }} />
                                        <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
                                        <Skeleton variant="text" width="60%" sx={{ fontSize: '0.8rem', mb: 2 }} />
                                        <Skeleton variant="text" width="80%" />
                                        <Skeleton variant="text" width="70%" />
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                ) : sortedCertificates.length === 0 ? (
                    /* Empty State */
                    <Paper
                        sx={{
                            py: 8,
                            px: 3,
                            textAlign: 'center',
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            boxShadow: 'none',
                        }}
                    >
                        <EmojiEventsIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                        <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 1 }}>
                            No certificates yet
                        </Typography>
                        <Typography sx={{ fontFamily, color: '#64748b', mb: 3 }}>
                            Complete courses to earn certificates. Start learning today!
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => navigate('/customer-panel/courses')}
                            sx={{
                                fontFamily,
                                fontWeight: 600,
                                textTransform: 'none',
                                bgcolor: ACCENT,
                                borderRadius: '12px',
                                px: 4,
                                boxShadow: 'none',
                                '&:hover': { bgcolor: '#059669' },
                            }}
                        >
                            Browse Courses
                        </Button>
                    </Paper>
                ) : (
                    <Grid container spacing={3}>
                        {sortedCertificates.map((cert, index) => (
                            <Grid item xs={12} sm={6} md={4} key={cert.id || cert._id || index}>
                                <CertificateCard
                                    certificate={cert}
                                    onView={handleView}
                                    onDownload={handleDownload}
                                    onShare={handleShare}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>
        </Box>
    );
}
